import { c as _c } from "react/compiler-runtime";
import { JSXElementConstructor, ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { ResponsiveContainer } from 'recharts';
import type { MouseHandlerDataParam } from 'recharts';
import styled, { useTheme } from 'styled-components';
import { capitalizeFirstLetter } from '../../../utils';
import isEmpty from '../../../utils/isEmpty';
import Button, { ClickableArea } from '../base/Button';
import { StyledCard, StyledCardSection } from '../base/Card';
import Icon, { IconName } from '../base/Icon';
import Text from '../base/Text';
import Tooltip, { TooltipCTA } from '../base/Tooltip';
import DropdownMenu from '../DropdownMenu';
import ScrollArea from '../helpers/ScrollArea';
import { Div } from '../helpers/StyledUtils';
import Loader from '../Loader';
import { MetricType, OrderBy, order_by_by_metric_type } from '../metrics/metric-definitions';
import MouseTooltip from '../MouseTooltip';
import WithLoader from '../WithLoader';
import ChartTooltip from './ChartTooltip';
import { ChartTimeRange, useChartInteractionGroup } from './ChartInteractionGroup';
import LegendColor from './LegendColor';
import SkeletonChart from './SkeletonChart';
export const CLASS_PREFIX = 'hookdeck-custom-recharts';
interface Data {
  [key: string]: string | number | undefined;
  name: string | number;
  sample_min?: string;
  sample_max?: string;
}
type ChartActiveName = string | number;
export type ChartDatapoint = {
  priority_display?: boolean;
  sample_range?: ChartTimeRange;
  x: number | string;
  y: number | string;
};
export type ChartDataSet = {
  key: string;
  label: string;
  mono?: boolean;
  theme_color?: 'primary' | 'success' | 'danger';
  hex_color?: string;
  data: ChartDatapoint[];
  total: number;
  highest: number;
  average: number;
  samples_with_data?: number;
  dashed?: boolean;
  connect_nulls?: boolean;
  metric_type: MetricType;
  getDataLabel?: (v: string | number) => React.ReactNode;
};
export interface ChartTimeOptions {
  time_unit: string;
  time_units: {
    key: string;
    label: string;
  }[];
  onTimeUnitSelected: (unit: string) => void;
  outline?: boolean;
  minimal?: boolean;
  expand_icon?: IconName;
}
export interface ChartMargin {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}
interface ChartConfig {
  id: string;
  data: Data[];
  cached_datasets: ChartDataSet[] | null;
  datasets: ChartDataSet[] | null;
  hovered: ChartHoveredState | null;
  active_name: ChartActiveName | null;
  locked_state: {
    hovered?: ChartHoveredState;
    active_name?: ChartActiveName;
  } | null;
  mousePosition?: {
    x: number;
    y: number;
  };
  height?: number;
  sync_id?: string;
  selection_range: {
    start: ChartActiveName;
    end: ChartActiveName;
  } | null;
  priority_display?: (string | number)[];
  setHovered: (hovered: ChartHoveredState | null) => void;
  setActiveName: (name: ChartActiveName | null) => void;
  onChartMouseDown: (state: ChartMouseState, event: React.MouseEvent<SVGGraphicsElement, MouseEvent>) => void;
  onChartMouseMove: (state: ChartMouseState | null) => void;
}
type ChartMouseState = MouseHandlerDataParam;
type ChartType = 'line' | 'bar';
export interface ChartProps {
  title?: string;
  variants?: {
    current_key: string;
    options: {
      label: string;
      key: string;
    }[];
    onChange: (variant: string) => void;
  };
  total?: number | string;
  total_hint?: string;
  title_tooltip?: React.ReactNode;
  title_tooltip_cta?: TooltipCTA;
  type: ChartType;
  legend_placeholder?: string;
  force_legend?: boolean;
  compact?: boolean;
  datasets: ChartDataSet[] | null;
  height?: number;
  loading?: boolean;
  children: (props: ChartConfig) => ReactElement<any, string | JSXElementConstructor<any>>;
  onRefresh?: () => void;
  time_options?: ChartTimeOptions;
  time_label?: React.ReactNode;
  action?: React.ReactNode;
}
export interface ChartHoveredState {
  key: string;
  label: string;
  name?: ChartActiveName;
  mono?: boolean;
  tooltip?: boolean;
  legend?: boolean;
}
const formatDataForRechart = (datasets: ChartDataSet[]): Data[] => {
  const merged_data: any = [];
  datasets.forEach(dataset => {
    dataset.data.forEach((data, i) => {
      if (!merged_data[i]) {
        merged_data[i] = {};
      }
      merged_data[i].name = data.x;
      if (data.sample_range) {
        merged_data[i].sample_min = data.sample_range.min;
        merged_data[i].sample_max = data.sample_range.max;
      }
      merged_data[i][dataset.label] = data.y;
    });
  });
  return merged_data;
};
const LegendTooltip = t0 => {
  const $ = _c(13);
  const {
    hovered,
    datasets
  } = t0;
  if (!hovered) {
    return null;
  }
  let label;
  let t1;
  let t2;
  if ($[0] !== datasets || $[1] !== hovered) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const dataset = datasets?.find(d => d.key === hovered.key);
      if (!dataset) {
        t2 = null;
        break bb0;
      }
      const t3 = order_by_by_metric_type[dataset.metric_type];
      let t4;
      if ($[5] !== t3) {
        t4 = capitalizeFirstLetter(t3);
        $[5] = t3;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      label = t4;
      t1 = dataset.getDataLabel?.(dataset[order_by_by_metric_type[dataset.metric_type]]);
    }
    $[0] = datasets;
    $[1] = hovered;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    label = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let value = t1;
  if (typeof value === "number" && isNaN(value)) {
    value = "N/A";
  }
  let t3;
  if ($[7] !== label || $[8] !== value) {
    t3 = [{
      label,
      value
    }];
    $[7] = label;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const data = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      px: 248
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== data) {
    t5 = <StyledCard raised={3} min_w={t4}>{data.map(_temp)}</StyledCard>;
    $[11] = data;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};
const legendSorter = (a: ChartDataSet, b: ChartDataSet, order_by: OrderBy) => {
  const a_value = a[order_by];
  const b_value = b[order_by];
  return a_value > b_value ? -1 : 1;
};
export const formatActiveNameForDom = (name: string | number) => {
  return String(name).replace(/[^a-zA-Z0-9_-]/g, '-');
};
export const ChartSampleCursor = t0 => {
  const $ = _c(5);
  const {
    points,
    x,
    y,
    width,
    height,
    stroke
  } = t0;
  const cursor_x = points?.[0]?.x ?? (Number.isFinite(x) ? x + (width || 0) / 2 : undefined);
  const cursor_top = points?.[0]?.y ?? y;
  const cursor_bottom = points?.[1]?.y ?? (Number.isFinite(y) ? y + height : undefined);
  if (![cursor_x, cursor_top, cursor_bottom].every(Number.isFinite)) {
    return null;
  }
  let t1;
  if ($[0] !== cursor_bottom || $[1] !== cursor_top || $[2] !== cursor_x || $[3] !== stroke) {
    t1 = <line x1={cursor_x} x2={cursor_x} y1={cursor_top} y2={cursor_bottom} stroke={stroke} strokeWidth={1} strokeDasharray="4 4" pointerEvents="none" />;
    $[0] = cursor_bottom;
    $[1] = cursor_top;
    $[2] = cursor_x;
    $[3] = stroke;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
const StyledResponsiveContainer = styled(ResponsiveContainer)`
  .recharts-wrapper,
  .recharts-surface {
    overflow: visible;
  }

  .recharts-surface > [class^='recharts-zIndex-layer_'] {
    outline: none;
  }

  .recharts-tooltip-cursor {
    fill: ${({
  theme
}) => theme.colors.outline.neutral};
  }
`;
const StyledLegendScrollContainer = styled(Div)<{
  $show_top_fade: boolean;
}>`
  position: relative;
  margin-top: ${({
  theme
}) => theme.spacing(-2)};

  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    left: 0;
    height: 16px;
    pointer-events: none;
    opacity: ${({
  $show_top_fade
}) => $show_top_fade ? 1 : 0};
    transition: opacity 150ms ease-in-out;
    background: linear-gradient(
      180deg,
      ${({
  theme
}) => theme.colors.surface.base.surface} 5%,
      transparent 100%
    );
  }
`;
const Chart: React.FC<ChartProps> = ({
  title,
  title_tooltip,
  title_tooltip_cta,
  force_legend,
  type,
  legend_placeholder,
  datasets,
  total,
  total_hint,
  height = 520,
  loading,
  children,
  onRefresh,
  time_options,
  time_label,
  action
}) => {
  // 'use no memo' — the `_children` memo below intentionally throttles recharts
  // re-renders with hand-tuned, narrower-than-exhaustive deps (bar charts skip hover
  // updates entirely; dataset selection compares by content). The compiler would widen
  // those deps to every captured value, re-rendering the full SVG tree on each
  // mousemove/poll, which visibly degrades chart smoothness.
  'use no memo';

  const id = useId().replace(/:/g, '');
  const theme = useTheme();
  const interaction_group = useChartInteractionGroup();
  const chart_tooltip_z_index = theme.zindex.dropdown - 2;
  const [hovered, setHovered] = useState<ChartHoveredState | null>(null);
  const [active_name, setActiveName] = useState<ChartActiveName | null>(null);
  const [last_hovered, setLastHovered] = useState<ChartHoveredState | null>(null);
  const [unselected_datasets, setUnselectedDatasets] = useState<{
    [key: string]: boolean;
  }>({});
  const [show_legend_tooltip, setShowLegendTooltip] = useState(false);
  const [legend_scrolled, setLegendScrolled] = useState(false);
  const [cached_datasets, setCachedDatasets] = useState(datasets);
  const [selection_range, setSelectionRange] = useState<{
    start: ChartActiveName;
    end: ChartActiveName;
  } | null>(null);
  const drag_state_ref = useRef<{
    start_index: number;
    end_index: number;
    start_range: ChartTimeRange;
    end_range: ChartTimeRange;
  } | null>(null);
  const suppress_click_ref = useRef(false);
  const [locked_state, setLockedState] = useState<{
    hovered?: ChartHoveredState;
    active_name?: ChartActiveName;
    lock_position: boolean;
    searched?: boolean;
  } | null>(null);
  const show_legend = datasets && datasets?.length > 1 || force_legend;
  const base_hovered = locked_state?.hovered || hovered;
  const _active_name = locked_state?.active_name ?? active_name;
  // Tooltip-sourced hover overrides key/name. Computed as a const up front — the previous
  // post-effect reassignment of `_hovered` made React Compiler bail on the whole component.
  const _hovered = hovered?.tooltip ? {
    ...hovered,
    ...(base_hovered || {}),
    key: hovered.key,
    name: _active_name ?? undefined
  } : base_hovered;
  let chart_height = height - 16; /* chart padding */

  if (title) {
    chart_height -= 40; // title height
  }
  if (show_legend) {
    chart_height -= 40; // legend height
  }
  if (total !== undefined) {
    chart_height -= 36; // total height
  }
  useEffect(() => {
    if (datasets) {
      setCachedDatasets(datasets?.filter(d => !unselected_datasets[d.key]));
    }
  }, [datasets, JSON.stringify(unselected_datasets)]);
  useEffect(() => {
    const css_property = type === 'line' ? 'stroke-opacity' : 'opacity';
    const faded_opacity = '0.3';
    const focused_opacity = '1';
    const chart_elements = document.body.querySelectorAll(`.${CLASS_PREFIX}-${id}`);
    const can_compare_datasets = (cached_datasets?.length ?? 0) > 1;
    if (can_compare_datasets && _active_name !== null && !_hovered) {
      const name = formatActiveNameForDom(_active_name);
      const hovered_position_elements = document.body.querySelectorAll(`.${CLASS_PREFIX}-${id}-${name}`);
      chart_elements?.forEach((el: HTMLElement) => {
        el.style[css_property] = faded_opacity;
      });
      hovered_position_elements?.forEach((el: HTMLElement) => {
        el.style[css_property] = focused_opacity;
      });
    } else if (can_compare_datasets && _active_name !== null && _hovered) {
      const hovered_position_elements = document.body.querySelectorAll(`.${CLASS_PREFIX}-${id}-${formatActiveNameForDom(_hovered.key)}`);
      chart_elements?.forEach((el: HTMLElement) => {
        el.style[css_property] = faded_opacity;
      });
      hovered_position_elements?.forEach((el: HTMLElement) => {
        el.style[css_property] = focused_opacity;
      });
    } else {
      chart_elements?.forEach((el: HTMLElement) => {
        el.style[css_property] = focused_opacity;
      });
    }
  }, [_hovered, _active_name]);
  useEffect(() => {
    if (_active_name === null) {
      setHovered(null);
      setLastHovered(null);
    }
  }, [_active_name]);
  useEffect(() => {
    if (_active_name !== null && _hovered?.key) {
      setLastHovered(_hovered);
    }
  }, [_active_name, _hovered?.key]);
  const data = useMemo(() => !cached_datasets ? [] : formatDataForRechart(cached_datasets), [cached_datasets]);
  const priority_display = cached_datasets && cached_datasets[0] && cached_datasets[0].data.filter(d => !!d.priority_display).map(d => d.x);
  let info: Record<string, string | number> = {};
  if (_active_name !== null) {
    const info_data = data.find(d => d.name === _active_name) as Data;
    if (info_data) {
      info = (cached_datasets || []).reduce((obj, dataset) => {
        const value = info_data[dataset.label];
        if (value === undefined) {
          return obj;
        }
        obj[dataset.label] = value;
        return obj;
      }, {});
      if (base_hovered && !base_hovered.tooltip && info[base_hovered.label] === undefined) {
        const hovered_value = info_data[base_hovered.label];
        if (hovered_value !== undefined) {
          info = {
            ...info,
            [base_hovered.label]: hovered_value
          };
        }
      }
    }
  }
  const is_loading = loading || !datasets;
  const tooltip_hovered = _hovered || last_hovered;
  const getActiveIndex = useCallback((state: ChartMouseState | null) => {
    if (!state) {
      return null;
    }
    const raw_index = state.activeIndex;
    if (typeof raw_index === 'number' && Number.isInteger(raw_index)) {
      return raw_index;
    }
    if (typeof raw_index === 'string' && raw_index !== '') {
      const index = Number(raw_index);
      if (Number.isInteger(index)) {
        return index;
      }
    }
    return state.activeLabel === undefined ? null : data.findIndex(datum => datum.name === state.activeLabel);
  }, [data]);
  const finishRangeSelection = useCallback(() => {
    const drag_state = drag_state_ref.current;
    drag_state_ref.current = null;
    setSelectionRange(null);
    if (!drag_state || drag_state.start_index === drag_state.end_index) {
      return;
    }
    suppress_click_ref.current = true;
    window.setTimeout(() => {
      suppress_click_ref.current = false;
    }, 0);
    const [first_range, last_range] = drag_state.start_index < drag_state.end_index ? [drag_state.start_range, drag_state.end_range] : [drag_state.end_range, drag_state.start_range];
    interaction_group?.onTimeRangeSelected?.({
      min: first_range.min,
      max: last_range.max
    });
  }, [interaction_group]);
  useEffect(() => {
    const onMouseUp = () => finishRangeSelection();
    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, [finishRangeSelection]);
  const onChartMouseDown = useCallback((state: ChartMouseState, event: React.MouseEvent<SVGGraphicsElement, MouseEvent>) => {
    if (!interaction_group?.onTimeRangeSelected || event.button !== 0) {
      return;
    }
    const index = getActiveIndex(state);
    const datum = index === null ? undefined : data[index];
    if (index === null || index < 0 || !datum?.sample_min || !datum.sample_max) {
      return;
    }
    const sample_range = {
      min: datum.sample_min,
      max: datum.sample_max
    };
    drag_state_ref.current = {
      start_index: index,
      end_index: index,
      start_range: sample_range,
      end_range: sample_range
    };
    setSelectionRange({
      start: datum.name,
      end: datum.name
    });
  }, [data, getActiveIndex, interaction_group?.onTimeRangeSelected]);
  const onChartMouseMove = useCallback((state: ChartMouseState | null) => {
    setActiveName(state?.activeLabel ?? null);
    const drag_state = drag_state_ref.current;
    if (!drag_state) {
      return;
    }
    const index = getActiveIndex(state);
    const datum = index === null ? undefined : data[index];
    if (index === null || index < 0 || !datum?.sample_min || !datum.sample_max || index === drag_state.end_index) {
      return;
    }
    drag_state.end_index = index;
    drag_state.end_range = {
      min: datum.sample_min,
      max: datum.sample_max
    };
    setSelectionRange({
      start: data[drag_state.start_index].name,
      end: datum.name
    });
  }, [data, getActiveIndex]);

  // Deliberate production render throttle for the recharts tree: bar charts do not
  // re-render on hover (only line charts key on `_hovered`), and dataset selection is
  // compared by content. In development, include the render prop so Fast Refresh can
  // replace chart markup without a full page reload.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _children = useMemo(() => children({
    id,
    data,
    cached_datasets,
    datasets,
    hovered: _hovered,
    active_name: _active_name,
    locked_state,
    sync_id: interaction_group?.sync_id,
    selection_range,
    priority_display: priority_display || undefined,
    setHovered,
    setActiveName,
    onChartMouseDown,
    onChartMouseMove
  }), [data, locked_state, selection_range, JSON.stringify(unselected_datasets), type === 'line' && _hovered, process.env.NODE_ENV === 'development' && children]);
  return <Div h={{
    px: height
  }} flex={{
    direction: 'column'
  }}>
      {title && <Div p={{
      x: 4,
      t: 4
    }} flex={{
      align: 'center',
      justify: 'space-between',
      gap: 2
    }}>
          <Div flex={{
        align: 'center',
        gap: 1
      }}>
            <Text semi>{title}</Text>
            {title_tooltip && <Tooltip tooltip={title_tooltip} cta={title_tooltip_cta}>
                <Icon muted icon="info" />
              </Tooltip>}
          </Div>
          {(onRefresh || time_options || time_label || action) && <Div flex={{
        gap: 2,
        align: 'center'
      }}>
              {time_options && time_options.time_units.length > 1 && <DropdownMenu outline={time_options?.outline || !time_options?.outline && !time_options?.minimal} ghost={time_options?.minimal || false} expand_icon={time_options?.expand_icon || 'expand_all'} placement="bottom-end" w={{
          px: 192
        }} options={time_options.time_units.map(unit => ({
          label: unit.label,
          selected: (time_options.time_unit || time_options.time_units[0].key) === unit.key,
          onClick: () => time_options.onTimeUnitSelected(unit.key)
        }))} label={time_options.time_unit ? time_options.time_units.find(u => u.key === time_options.time_unit)?.label : time_options.time_units[0].label} /> || time_label && time_label}
              {onRefresh && <Button outline icon="reload" onClick={() => {
          onRefresh && onRefresh();
        }} />}
              {action}
            </Div>}
        </Div>}
      {total !== undefined && <Div p={{
      x: 4,
      t: 0
    }} flex={{
      align: 'flex-end',
      gap: 1
    }}>
          {typeof total === 'number' ? <NumberFormat renderText={v => <Text semi size="xl">
                  {v}
                </Text>} displayType="text" value={total} thousandSeparator={','} /> : <Text semi size="xl">
              {total}
            </Text>}
          {total_hint && <Text muted size="xs" m={{
        b: 0.25
      }}>
              {total_hint}
            </Text>}
        </Div>}
      <WithLoader loading={is_loading} loading_element={<SkeletonChart type={type} legend_placeholder={legend_placeholder} height={height} />} long_loading_element={<Loader h={{
      px: height
    }} message={'Loading your data is taking a bit longer than usual. It should be ready in a few moments.'} />}>
        <Div flex={{
        grow: true
      }} p={{
        t: 4,
        b: 3,
        r: 4
      }} h={{
        px: chart_height
      }} onMouseMove={e => {
        const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
        if (!_hovered || _hovered?.name === active_name || target.classList.contains(`$[CLASS_PREFIX}-${id}`)) {
          return;
        }
        setHovered(null);
      }} onMouseLeave={() => {
        setHovered(null);
        setActiveName(null);
        setLockedState(null);
      }} onClick={() => {
        if (suppress_click_ref.current) {
          suppress_click_ref.current = false;
          return;
        }
        setLockedState((prev: any) => {
          if (!prev) {
            return {
              active_name,
              lock_position: true
            };
          }
          if (prev.lock_position && prev.searched) {
            return {
              ...prev,
              active_name: undefined,
              lock_position: false
            };
          }
          if (prev.searched) {
            return {
              ...prev,
              active_name,
              lock_position: true
            };
          }
          return null;
        });
      }}>
          <StyledResponsiveContainer>{_children}</StyledResponsiveContainer>
          <MouseTooltip visible={!isEmpty(info)} locked={!!locked_state?.lock_position} z_index={chart_tooltip_z_index}>
            <ChartTooltip label={_active_name!} mono={cached_datasets?.[0]?.mono === false ? false : true} datasets={cached_datasets!} hovered={tooltip_hovered?.key} sort_hovered={!!tooltip_hovered && !tooltip_hovered.tooltip && !tooltip_hovered.legend} data={Object.entries(info)} locked={!!locked_state?.lock_position} setHover={setHovered} setLockedState={setLockedState as any} />
          </MouseTooltip>
        </Div>
        {datasets && show_legend && <StyledLegendScrollContainer $show_top_fade={legend_scrolled}>
            <ScrollArea flex={{
          wrap: true
        }} p={{
          x: 4
        }} onScroll={e => setLegendScrolled(e.currentTarget.scrollTop > 0)} style={{
          overflow: 'scroll',
          minHeight: '36px',
          maxHeight: '36px',
          rowGap: '4px',
          columnGap: '8px',
          paddingTop: '8px'
        }}>
              {datasets.map(dataset => <ClickableArea p={{
            x: dataset.mono ? 2 : 1,
            y: 0
          }} block={false} style={{
            opacity: unselected_datasets[dataset.key] ? 0.5 : 1,
            height: 'fit-content'
          }} key={dataset.key} flex={{
            align: 'center'
          }} rounded onClick={() => {
            if (cached_datasets && cached_datasets?.length <= 1 && !unselected_datasets[dataset.key]) {
              return;
            }
            setUnselectedDatasets(prev => {
              if (prev[dataset.key]) {
                delete prev[dataset.key];
              } else {
                prev[dataset.key] = true;
              }
              return {
                ...prev
              };
            });
          }} onMouseEnter={() => {
            setHovered({
              key: dataset.key,
              label: dataset.label,
              mono: dataset.mono === false ? false : true,
              legend: true
            });
            setShowLegendTooltip(true);
          }} onMouseLeave={() => {
            setHovered(null);
            setShowLegendTooltip(false);
          }}>
                  <LegendColor color={dataset.theme_color ? theme.colors.surface.chart[dataset.theme_color] : dataset.hex_color} m={{
              r: 2
            }} />
                  <Text ellipsis mono={dataset.mono === false ? false : true} size="xs">
                    {dataset.label}
                  </Text>
                </ClickableArea>)}
            </ScrollArea>
          </StyledLegendScrollContainer>}
      </WithLoader>
      <MouseTooltip visible={show_legend_tooltip} z_index={chart_tooltip_z_index}>
        <LegendTooltip hovered={_hovered} datasets={datasets} />
      </MouseTooltip>
    </Div>;
};
export default Chart;
function _temp(t0) {
  const {
    label: label_0,
    value: value_0
  } = t0;
  return <StyledCardSection flex={{
    align: "center",
    justify: "space-between"
  }} p={{
    x: 3,
    y: 2
  }} key={label_0}><Text semi={true} m={{
      r: 2
    }}>{label_0}</Text><Text semi={true}>{value_0}</Text></StyledCardSection>;
}